import { useState } from "react";
import { Settings as SettingsIcon, User, CreditCard, Bell, Calendar, Mail, Crown, ExternalLink, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function Settings() {
  const { user, subscription, checkSubscription } = useAuth();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    productAnnouncements: true,
    weeklyDigest: false,
    liveCallReminders: true,
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (error) throw error;

      toast.success("Password updated successfully");
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error === "no_customer") {
        toast.info(data.message || "No billing history found");
        return;
      }
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to open billing portal");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleRefreshSubscription = async () => {
    await checkSubscription();
    toast.success("Subscription status refreshed");
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
          <SettingsIcon className="w-6 h-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your account, subscription, and preferences</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-muted/50">
          <TabsTrigger value="account" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Account</span>
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Subscription</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <div className="glass-card p-6">
            <h2 className="font-semibold text-lg mb-4">Profile Information</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-muted/50"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Contact support to change your email address.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-id">User ID</Label>
                <Input
                  id="user-id"
                  value={user?.id || ""}
                  disabled
                  className="bg-muted/50 font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label>Account Created</Label>
                <p className="text-sm text-muted-foreground">
                  {user?.created_at ? formatDate(user.created_at) : "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <h2 className="font-semibold text-lg">Change Password</h2>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                    className="pr-10"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="pr-10"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isChangingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              >
                {isChangingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Password
              </Button>
            </form>
          </div>

          <div className="glass-card p-6 border-destructive/20">
            <h2 className="font-semibold text-lg mb-2 text-destructive">Danger Zone</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Permanently delete your account and all associated data.
            </p>
            <Button variant="destructive" size="sm" disabled>
              Delete Account
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Contact support to delete your account.
            </p>
          </div>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-6">
          <div className="glass-card p-6 border-primary/20">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-purple to-neon-yellow flex items-center justify-center">
                  <Crown className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">AI StoryCraft Studio</h2>
                  <p className="text-sm text-muted-foreground">Your current membership</p>
                </div>
              </div>
              {subscription.isLifetime ? (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Lifetime Member
                </span>
              ) : (
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  subscription.status === "active" 
                    ? "bg-green-500/20 text-green-400" 
                    : subscription.status === "past_due"
                      ? "bg-yellow-500/20 text-yellow-400"
                      : "bg-destructive/20 text-destructive"
                }`}>
                  {subscription.status === "active" ? "Active" : 
                   subscription.status === "past_due" ? "Past Due" : 
                   subscription.status || "Inactive"}
                </span>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Plan</p>
                <p className="font-semibold">
                  {subscription.isLifetime 
                    ? "Lifetime Access" 
                    : subscription.plan === "yearly" 
                      ? "Annual ($999/year)" 
                      : "Monthly ($99/month)"}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">
                  {subscription.isLifetime ? "Access" : subscription.status === "canceled" ? "Access Until" : "Next Billing"}
                </p>
                <p className="font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {subscription.isLifetime 
                    ? "Forever" 
                    : formatDate(subscription.subscriptionEnd)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {!subscription.isLifetime && (
                <Button onClick={handleManageSubscription} disabled={isLoadingPortal}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {isLoadingPortal ? "Opening..." : "Manage Billing"}
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              )}
              <Button variant="outline" onClick={handleRefreshSubscription}>
                Refresh Status
              </Button>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">Subscription Details</h3>
            <div className="space-y-3 text-sm">
              {subscription.stripeCustomerId && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Customer ID</span>
                  <span className="font-mono">{subscription.stripeCustomerId}</span>
                </div>
              )}
              {subscription.stripeSubscriptionId && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Subscription ID</span>
                  <span className="font-mono">{subscription.stripeSubscriptionId}</span>
                </div>
              )}
              {subscription.productId && (
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Product ID</span>
                  <span className="font-mono">{subscription.productId}</span>
                </div>
              )}
            </div>
          </div>

          {/* What's Included */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">What's Included</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Unlimited access to Nexis AI script generator
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Monetization AI product recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                AskFint AI mentor
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Live coaching calls & recordings
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Community access
              </li>
            </ul>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <div className="glass-card p-6">
            <h2 className="font-semibold text-lg mb-4">Email Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Product Updates</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified about new features and improvements
                  </p>
                </div>
                <Switch
                  checked={notifications.emailUpdates}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({ ...prev, emailUpdates: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-2 border-t border-border">
                <div>
                  <p className="font-medium">Product Announcements</p>
                  <p className="text-sm text-muted-foreground">
                    Important announcements and updates
                  </p>
                </div>
                <Switch
                  checked={notifications.productAnnouncements}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({ ...prev, productAnnouncements: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-2 border-t border-border">
                <div>
                  <p className="font-medium">Weekly Digest</p>
                  <p className="text-sm text-muted-foreground">
                    Weekly summary of community activity
                  </p>
                </div>
                <Switch
                  checked={notifications.weeklyDigest}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({ ...prev, weeklyDigest: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-2 border-t border-border">
                <div>
                  <p className="font-medium">Live Call Reminders</p>
                  <p className="text-sm text-muted-foreground">
                    Get reminded before scheduled coaching calls
                  </p>
                </div>
                <Switch
                  checked={notifications.liveCallReminders}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({ ...prev, liveCallReminders: checked }))
                  }
                />
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <Button disabled>
                Save Preferences
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Notification preferences coming soon.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
